// 条件必填：某个文件 / 字段只在另一个字段取某个值时才必填。
// 例：ICA 申诉「担保人是否在职」= 是 → 担保人在职证明（PDF）、雇主、职业变成必填。
import type { ManifestSchema, RequiredWhen } from "./types.js";

const YES = new Set(["true", "yes", "y", "是", "1"]);
const NO = new Set(["false", "no", "n", "否", "0"]);

/** 条件是否成立：布尔值按 是 / Yes / true 宽松比较，其它按文字（忽略大小写和首尾空格）比较 */
export function conditionMet(w: RequiredWhen | undefined, manifest: Record<string, unknown>): boolean {
  if (!w) return false;
  const v = manifest[w.field];
  if (v === undefined || v === null || v === "") return false;
  const s = String(v).trim().toLowerCase();
  if (typeof w.equals === "boolean") return w.equals ? YES.has(s) : NO.has(s);
  return s === String(w.equals).trim().toLowerCase();
}

/** 由文件决定的字段（fromFile）：传了那类文件 = "true"，没传 = "false"，覆盖手填 / 提取的值 */
export function applyFileFlags(schema: ManifestSchema, manifest: Record<string, unknown>, uploadedKeys: string[]): Record<string, unknown> {
  const flags = (schema.fields ?? []).filter((f) => f.fromFile);
  if (!flags.length) return manifest;
  const m = { ...manifest };
  for (const f of flags) m[f.key] = uploadedKeys.includes(f.fromFile!) ? "true" : "false";
  return m;
}

/** 字段由哪个文件决定的人话：「看有没有传「担保人在职证明」」 */
export function fromFileText(fromFile: string, schema: ManifestSchema): string {
  return `看有没有传「${schema.files?.find((x) => x.key === fromFile)?.label ?? fromFile}」：传了就是「是」，没传就是「否」`;
}

/** 按当前取值算出实际必填：required 本身为真，或条件成立 */
export function resolveRequired(schema: ManifestSchema, manifest: Record<string, unknown>): ManifestSchema {
  return {
    ...schema,
    fields: (schema.fields ?? []).map((f) => (f.requiredWhen && !f.required && conditionMet(f.requiredWhen, manifest) ? { ...f, required: true } : f)),
    files: (schema.files ?? []).map((r) => (r.requiredWhen && !r.required && conditionMet(r.requiredWhen, manifest) ? { ...r, required: true } : r)),
  };
}

/** 条件的人话：「担保人是否在职 为 是 时必填」 */
export function conditionText(w: RequiredWhen, schema: ManifestSchema): string {
  const label = schema.fields.find((f) => f.key === w.field)?.label ?? w.field;
  const val = typeof w.equals === "boolean" ? (w.equals ? "是" : "否") : w.equals;
  return `「${label}」为「${val}」时必填`;
}

/** 文件类别的门户大小上限（字节）：类别自己设的（没设 = 不上传给门户，不限）；「其他材料」这类不在清单里的用这个类型里最小的上限；都没设 = 0 */
export function fileLimitBytes(schema: ManifestSchema, key: string): number {
  const cat = schema.files?.find((f) => f.key === key);
  if (cat) return cat.maxKB ? cat.maxKB * 1024 : 0;
  const all = (schema.files ?? []).map((f) => f.maxKB ?? 0).filter((x) => x > 0);
  return all.length ? Math.min(...all) * 1024 : 0;
}
